/**
 * Universal export helpers for tabular data.
 *
 * - exportToExcel(filename, sheets)  → downloads an .xlsx via SheetJS
 * - exportToPDF(filename, opts)      → downloads a premium monochromatic PDF
 *
 * PDF design contract (see /app/memory/Rediseño PDF.md):
 *   • Palette: black #000, white #fff, greys #F3F3F3 / #D9D9D9 / #666666.
 *   • Font: Helvetica (used as Inter alternative — jsPDF's default embed).
 *   • Type ramp:
 *       businessName ...... 10 pt Semibold
 *       title "Reporte de caja" 22 pt Bold
 *       period / metadata . 9.5 pt Regular
 *       KPI label ........... 8 pt Regular  (uppercase, letter-spaced 0.4)
 *       KPI value ......... 16 pt Bold
 *       section number .... 10 pt Bold (white on black square)
 *       section title ..... 11 pt Bold
 *       subtitle .......... 9.5 pt Semibold (uppercase)
 *       body / values ..... 9.5 pt
 *       table head ........ 9 pt Semibold (white on black)
 *       footer ............ 8 pt Regular #666666
 *   • Margins: 18 mm sides, 16 mm top/bottom.
 *   • Numbers right-aligned, labels left-aligned, totals bold with top rule.
 *   • Automatic page breaks; table headers repeat.
 *
 * opts (PDF):
 *   {
 *     businessName, title, periodLabel, rightSubtitle?, rightMeta?,
 *     kpis?: [{label, value}],
 *     sections: [
 *       { number: '01', title: '...', groups?: [{subtitle, items}], columns?, rows?, note? }
 *     ]
 *   }
 *
 *   Legacy callers may still pass:
 *   { businessName, title, subtitle, sections: [{title, summary}, ...] }
 *   and the renderer will coerce them.
 */
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const _now = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const _safeFile = (name) => `${name}_${_now()}`.replace(/[^a-zA-Z0-9_\-]/g, '_');

// Layout constants (mm)
const MARGIN_X = 18;
const MARGIN_TOP = 16;
const MARGIN_BOTTOM = 16;

// Palette
const C_BLACK = [0, 0, 0];
const C_WHITE = [255, 255, 255];
const C_GREY_LIGHT = [217, 217, 217]; // #D9D9D9 — table dividers
const C_GREY_MID = [102, 102, 102];   // #666666 — footer / meta

// Remove any "%" sign but keep "$"; also strip broken " !’" sequences that the
// caller might inject when building period labels.
const _sanitize = (v) => {
  if (v === null || v === undefined) return '';
  return String(v).replace(/\s*%\s*/g, '').replace(/![’']/g, 'al').trim();
};

const _NUMERIC_HEADERS = new Set([
  'monto', 'total', 'valor', 'cantidad', 'tickets', 'servicios',
  'productos', 'clientes', 'visitas', 'horas', 'horas trabajadas',
  'ticket promedio', 'servicios por hora', 'precio', 'importe', 'subtotal',
  'promedio',
]);
const _isNumericHeader = (h = '') => _NUMERIC_HEADERS.has(String(h).toLowerCase().trim());

// Detects whether a text value looks like a money/number amount so decimals
// can be rendered with a monospaced font and stay column-aligned.
const _isMoneyLike = (v) => {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  if (!s) return false;
  // Matches "$13,140.00", "-$292.00", "1,234", "0.222", etc.
  return /^-?\$?-?\$?\d{1,3}(?:,\d{3})*(?:\.\d+)?$/.test(s) || /^-?\$?\d+\.\d+$/.test(s) || /^\d+$/.test(s);
};

export function exportToExcel(baseName, sheets) {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) => {
    const header = s.columns.map((c) => c.header);
    const body = s.rows.map((r) => s.columns.map((c) => r[c.key] ?? ''));
    const aoa = [header, ...body];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = s.columns.map((c) => ({
      wch: Math.max(
        c.header.length,
        ...body.map((r, i) => String(r[s.columns.indexOf(c)] ?? '').length)
      ) + 2,
    }));
    XLSX.utils.book_append_sheet(wb, ws, (s.name || 'Hoja').slice(0, 31));
  });
  XLSX.writeFile(wb, `${_safeFile(baseName)}.xlsx`);
}

// ---------- PDF helpers ----------

function _pageWidth(doc) { return doc.internal.pageSize.getWidth(); }
function _pageHeight(doc) { return doc.internal.pageSize.getHeight(); }
function _contentWidth(doc) { return _pageWidth(doc) - MARGIN_X * 2; }

function _drawHeader(doc, { businessName, title, periodLabel, rightSubtitle, rightMeta }) {
  const pw = _pageWidth(doc);
  doc.setTextColor(...C_BLACK);

  // Business name (top-left, small)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text((businessName || '').toUpperCase(), MARGIN_X, MARGIN_TOP + 3);

  // Report title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(title || 'Reporte de caja', MARGIN_X, MARGIN_TOP + 14);

  // Period
  if (periodLabel) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(_sanitize(periodLabel), MARGIN_X, MARGIN_TOP + 20);
  }

  // Right-hand metadata block (subtitle + meta line)
  if (rightSubtitle) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(String(rightSubtitle).toUpperCase(), pw - MARGIN_X, MARGIN_TOP + 3, { align: 'right' });
  }
  if (rightMeta) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...C_GREY_MID);
    doc.text(_sanitize(rightMeta), pw - MARGIN_X, MARGIN_TOP + 8, { align: 'right' });
    doc.setTextColor(...C_BLACK);
  }

  // Thin rule under the header
  doc.setDrawColor(...C_BLACK);
  doc.setLineWidth(0.25);
  doc.line(MARGIN_X, MARGIN_TOP + 25, pw - MARGIN_X, MARGIN_TOP + 25);

  return MARGIN_TOP + 32; // starting cursorY for the first section / KPI row
}

function _drawCompactHeader(doc, { businessName, title, periodLabel }) {
  const pw = _pageWidth(doc);
  doc.setTextColor(...C_BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text((businessName || '').toUpperCase(), MARGIN_X, MARGIN_TOP);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(_sanitize(title || 'Reporte de caja'), MARGIN_X, MARGIN_TOP + 5);
  if (periodLabel) {
    doc.text(_sanitize(periodLabel).toUpperCase(), pw - MARGIN_X, MARGIN_TOP + 5, { align: 'right' });
  }
  doc.setDrawColor(...C_GREY_LIGHT);
  doc.setLineWidth(0.2);
  doc.line(MARGIN_X, MARGIN_TOP + 8, pw - MARGIN_X, MARGIN_TOP + 8);
  return MARGIN_TOP + 14;
}

function _drawKPIRow(doc, startY, kpis) {
  if (!kpis || !kpis.length) return startY;
  const w = _contentWidth(doc);
  const gap = 3;
  const bw = (w - gap * (kpis.length - 1)) / kpis.length;
  const bh = 20;
  kpis.forEach((k, i) => {
    const x = MARGIN_X + i * (bw + gap);
    // Border box
    doc.setDrawColor(...C_BLACK);
    doc.setLineWidth(0.35);
    doc.rect(x, startY, bw, bh);
    // Label (top)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C_GREY_MID);
    doc.text(String(k.label || '').toUpperCase(), x + 3, startY + 5);
    // Value (bottom)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...C_BLACK);
    doc.text(_sanitize(k.value ?? ''), x + bw - 3, startY + 15, { align: 'right' });
  });
  doc.setTextColor(...C_BLACK);
  return startY + bh + 8;
}

function _drawSectionHeader(doc, y, section) {
  const pw = _pageWidth(doc);
  const number = section.number ? String(section.number) : '';
  const boxSize = 8;
  let x = MARGIN_X;

  if (number) {
    // Filled black square with white centered number
    doc.setFillColor(...C_BLACK);
    doc.rect(x, y - boxSize + 1.8, boxSize, boxSize, 'F');
    doc.setTextColor(...C_WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(number, x + boxSize / 2, y - 0.6, { align: 'center' });
    x += boxSize + 4;
  }

  // Title
  doc.setTextColor(...C_BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(String(section.title || '').toUpperCase(), x, y);

  // Thin horizontal rule underneath
  doc.setDrawColor(...C_BLACK);
  doc.setLineWidth(0.35);
  doc.line(MARGIN_X, y + 3, pw - MARGIN_X, y + 3);

  return y + 9;
}

function _drawGroup(doc, startY, group) {
  let y = startY;

  if (group.subtitle) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C_GREY_MID);
    doc.text(String(group.subtitle).toUpperCase(), MARGIN_X, y);
    doc.setTextColor(...C_BLACK);
    y += 4;
  }

  const items = group.items || [];
  const hasHint = items.some((it) => it.hint);
  const columns = hasHint
    ? [
        { header: '', dataKey: 'label' },
        { header: '', dataKey: 'hint' },
        { header: '', dataKey: 'value' },
      ]
    : [
        { header: '', dataKey: 'label' },
        { header: '', dataKey: 'value' },
      ];
  const rows = items.map((it) => ({
    label: _sanitize(it.label),
    hint: _sanitize(it.hint || ''),
    value: _sanitize(it.value),
    _isTotal: !!it.isTotal,
  }));

  autoTable(doc, {
    startY: y,
    head: [],
    body: rows.map((r) => columns.map((c) => r[c.dataKey])),
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 10,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 0 },
      textColor: C_BLACK,
      lineColor: C_GREY_LIGHT,
      lineWidth: 0,
    },
    columnStyles: hasHint
      ? {
          0: { cellWidth: 82, halign: 'left' },
          1: { halign: 'left', textColor: C_GREY_MID, fontSize: 9 },
          2: { halign: 'right', cellWidth: 30 },
        }
      : {
          0: { halign: 'left' },
          1: { halign: 'right', cellWidth: 30 },
        },
    didParseCell: (data) => {
      const row = rows[data.row.index];
      const isValueCol = data.column.index === columns.length - 1;
      // Monospace ONLY the value column so decimals stack vertically.
      if (isValueCol && _isMoneyLike(data.cell.raw)) {
        data.cell.styles.font = 'courier';
        data.cell.styles.fontSize = 9.5;
      }
      // Subtle bottom rule between rows (skip the last one which sits on its own).
      if (data.section === 'body' && data.row.index < rows.length - 1) {
        data.cell.styles.lineWidth = {
          ...(data.cell.styles.lineWidth || {}),
          bottom: 0.1,
        };
      }
      if (row && row._isTotal) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.lineWidth = { top: 0.4 };
        data.cell.styles.lineColor = C_BLACK;
        data.cell.styles.cellPadding = { top: 3.5, right: 4, bottom: 3, left: 0 };
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
  });
  return doc.lastAutoTable.finalY + 3;
}

function _drawTable(doc, startY, section) {
  const cols = section.columns;
  autoTable(doc, {
    startY,
    head: [cols.map((c) => c.header)],
    body: section.rows.map((r) => cols.map((c) => _sanitize(r[c.key]))),
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 10,
      cellPadding: { top: 3.5, right: 4, bottom: 3.5, left: 4 },
      textColor: C_BLACK,
      lineColor: C_GREY_LIGHT,
      lineWidth: { bottom: 0.1 },
      minCellHeight: 7,
    },
    headStyles: {
      fontStyle: 'bold',
      fontSize: 9,
      textColor: C_WHITE,
      fillColor: C_BLACK,
      lineWidth: 0,
      cellPadding: { top: 3.5, right: 1.5, bottom: 3.5, left: 1.5 },
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'center',
    },
    columnStyles: cols.reduce((acc, c, i) => {
      const isNum = _isNumericHeader(c.header);
      const align = c.align || (isNum ? 'right' : 'left');
      acc[i] = { halign: align };
      if (c.width) acc[i].cellWidth = c.width;
      // Ensure numeric columns can fit "$2,480.00" in Courier body font
      // without eating up the width of the first (name/label) column.
      if (isNum && !c.width) acc[i].minCellWidth = 20;
      return acc;
    }, {}),
    didParseCell: (data) => {
      // Head always in Helvetica bold — used both for width calc and rendering.
      if (data.section === 'head') return;
      // Body: Courier for money-like cells so decimals stack vertically.
      // Use a slightly smaller size + tighter padding so 9-char amounts fit
      // tight columns without wrapping.
      if (data.section === 'body' && _isMoneyLike(data.cell.raw)) {
        data.cell.styles.font = 'courier';
        data.cell.styles.fontSize = 8.5;
        data.cell.styles.cellPadding = { top: 3.5, right: 2, bottom: 3.5, left: 2 };
      }
      const raw = section.rows[data.row.index];
      if (data.section === 'body' && raw) {
        const firstVal = String(raw[cols[0].key] ?? '').trim().toUpperCase();
        if (firstVal === 'TOTAL') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.lineWidth = { top: 0.4, bottom: 0.1 };
          data.cell.styles.lineColor = C_BLACK;
        }
      }
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
  });
  let finalY = doc.lastAutoTable.finalY;

  if (section.note) {
    finalY += 3;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...C_GREY_MID);
    const lines = doc.splitTextToSize(_sanitize(section.note), _contentWidth(doc));
    doc.text(lines, MARGIN_X, finalY);
    doc.setTextColor(...C_BLACK);
    finalY += lines.length * 3.5;
  }

  return finalY + 3;
}

function _drawFooter(doc, generatedAt) {
  const pw = _pageWidth(doc);
  const ph = _pageHeight(doc);
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    // Thin separator
    doc.setDrawColor(...C_GREY_LIGHT);
    doc.setLineWidth(0.15);
    doc.line(MARGIN_X, ph - 12, pw - MARGIN_X, ph - 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C_GREY_MID);
    doc.text(`Generado ${generatedAt}`, MARGIN_X, ph - 7);
    doc.text(`Página ${i} de ${total}`, pw - MARGIN_X, ph - 7, { align: 'right' });
  }
}

export function exportToPDF(baseName, opts) {
  const {
    businessName, title, periodLabel, subtitle,
    rightSubtitle, rightMeta, kpis, sections = [],
  } = opts;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const generatedAt = new Date().toLocaleString('es-MX', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'America/Mexico_City',
  })
    // Normalise "a.m." / "p.m." → "a. m." / "p. m." per style guide
    .replace(/\ba\.?\s?m\.?/i, 'a. m.')
    .replace(/\bp\.?\s?m\.?/i, 'p. m.');

  let cursorY = _drawHeader(doc, {
    businessName,
    title: title || 'Reporte de caja',
    periodLabel: periodLabel || subtitle,
    rightSubtitle,
    rightMeta: rightMeta || generatedAt,
  });

  if (kpis && kpis.length) {
    cursorY = _drawKPIRow(doc, cursorY, kpis);
  }

  const pageHeight = _pageHeight(doc);

  sections.forEach((section, idx) => {
    if (!section) return;

    if (idx > 0) cursorY += 6;

    // If we're near the bottom, break to a new page. Also start the employee
    // section on a fresh page so it never wraps a single row.
    const isBigTable = section.columns && section.rows && section.rows.length > 6;
    if (cursorY > pageHeight - (isBigTable ? 80 : 40)) {
      doc.addPage();
      cursorY = _drawCompactHeader(doc, { businessName, title, periodLabel: periodLabel || subtitle });
    }

    cursorY = _drawSectionHeader(doc, cursorY, section);

    // Legacy 'summary' coming as strings — convert to a single unnamed group.
    let groups = section.groups;
    if (!groups && Array.isArray(section.summary) && section.summary.length) {
      const items = section.summary.map((it) => {
        if (typeof it === 'string') {
          const clean = it.replace(/─+/g, '').trim();
          if (!clean) return null;
          const parts = clean.split(/\s{2,}/);
          if (parts.length >= 2) return { label: parts[0], value: parts[parts.length - 1] };
          return { label: clean, value: '' };
        }
        return it;
      }).filter(Boolean);
      groups = [{ items }];
    }

    if (groups) {
      groups.forEach((g) => {
        // Break if next group won't fit
        if (cursorY > pageHeight - 30) {
          doc.addPage();
          cursorY = _drawCompactHeader(doc, { businessName, title, periodLabel: periodLabel || subtitle });
        }
        cursorY = _drawGroup(doc, cursorY, g);
      });
    }

    if (section.columns && section.rows) {
      cursorY = _drawTable(doc, cursorY, section);
    }
  });

  _drawFooter(doc, generatedAt);
  doc.save(`${_safeFile(baseName)}.pdf`);
}
